/**
 * ============================================================================
 * DEVELOPER-ONLY: Pro Access Override Context
 * ============================================================================
 * 
 * WARNING: This is a development/testing tool ONLY.
 * It must NEVER be exposed in production environments.
 * 
 * Purpose:
 * - Allows developers to test Pro-gated features without real subscriptions
 * - Enables testing of both locked (free) and unlocked (Pro) states
 * 
 * Usage:
 * - Access the override control via /dev/acceptance page
 * - Override values are stored in localStorage for persistence
 * - Backend requests include X-Dev-Pro-Override header when active
 * 
 * Security:
 * - Only works when NODE_ENV !== 'production' OR REACT_APP_DEV_MODE === 'true'
 * - Backend validates dev mode before honoring the override
 * ============================================================================
 */

import { createContext, useContext, useState, useEffect, useCallback } from "react";

const DevProOverrideContext = createContext(null);

// Override modes
export const PRO_OVERRIDE_MODES = {
  NORMAL: "normal",      // Use actual subscription state
  FORCE_PRO: "force_pro",    // Force Pro access (unlocked)
  FORCE_FREE: "force_free",  // Force Free access (locked)
};

// Check if we're in a development environment
const isDevEnvironment = () => {
  // Explicit dev mode flag takes precedence
  if (process.env.REACT_APP_DEV_MODE === 'true') {
    return true;
  }
  
  // Development builds always allow
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }
  
  // Preview/staging URLs (Emergent preview environments)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('preview.emergentagent.com') || 
        hostname.includes('localhost') ||
        hostname.includes('127.0.0.1')) {
      return true;
    }
  }
  
  return false;
};

const STORAGE_KEY = "macromeal_dev_pro_override";

export const useDevProOverride = () => {
  const context = useContext(DevProOverrideContext);
  if (!context) {
    // Return safe defaults if context not available (e.g., in production)
    return {
      isDevMode: false,
      overrideMode: PRO_OVERRIDE_MODES.NORMAL,
      setOverrideMode: () => {},
      getEffectiveProStatus: (actualStatus) => actualStatus,
      getRequestHeaders: () => ({}),
    };
  }
  return context;
};

export const DevProOverrideProvider = ({ children }) => {
  const [isDevMode] = useState(isDevEnvironment);
  const [overrideMode, setOverrideModeState] = useState(PRO_OVERRIDE_MODES.NORMAL);

  // Load persisted override from localStorage
  useEffect(() => {
    if (!isDevMode) return;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && Object.values(PRO_OVERRIDE_MODES).includes(stored)) {
        setOverrideModeState(stored);
      }
    } catch (e) {
      // localStorage not available, use default
      console.warn("[DevProOverride] Could not read from localStorage");
    }
  }, [isDevMode]);

  // Persist override to localStorage
  const setOverrideMode = useCallback((mode) => {
    if (!isDevMode) return;
    
    if (!Object.values(PRO_OVERRIDE_MODES).includes(mode)) {
      console.warn("[DevProOverride] Invalid mode:", mode);
      return;
    }
    
    setOverrideModeState(mode);
    
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      console.warn("[DevProOverride] Could not persist to localStorage");
    }
  }, [isDevMode]);

  /**
   * Get the effective Pro status based on override mode
   * @param {boolean} actualIsPro - The real subscription Pro status
   * @param {boolean} actualIsTrial - The real trial status
   * @returns {{ isPro: boolean, isTrial: boolean, isOverridden: boolean }}
   */
  const getEffectiveProStatus = useCallback((actualIsPro, actualIsTrial = false) => {
    if (!isDevMode || overrideMode === PRO_OVERRIDE_MODES.NORMAL) {
      return {
        isPro: actualIsPro,
        isTrial: actualIsTrial,
        isOverridden: false,
      };
    }
    
    if (overrideMode === PRO_OVERRIDE_MODES.FORCE_PRO) {
      return {
        isPro: true,
        isTrial: false,
        isOverridden: true,
      };
    }
    
    if (overrideMode === PRO_OVERRIDE_MODES.FORCE_FREE) {
      return {
        isPro: false,
        isTrial: false,
        isOverridden: true,
      };
    }
    
    return { isPro: actualIsPro, isTrial: actualIsTrial, isOverridden: false };
  }, [isDevMode, overrideMode]);

  /**
   * Get headers to include in API requests for backend override support
   * @returns {Object} Headers object
   */
  const getRequestHeaders = useCallback(() => {
    if (!isDevMode || overrideMode === PRO_OVERRIDE_MODES.NORMAL) {
      return {};
    }
    
    // Send override header to backend
    return {
      "X-Dev-Pro-Override": overrideMode,
    };
  }, [isDevMode, overrideMode]);

  const value = {
    isDevMode,
    overrideMode,
    setOverrideMode,
    getEffectiveProStatus,
    getRequestHeaders,
    PRO_OVERRIDE_MODES, // Export modes for convenience
  };

  return (
    <DevProOverrideContext.Provider value={value}>
      {children}
    </DevProOverrideContext.Provider>
  );
};

export default DevProOverrideContext;
